/* eslint-disable react/require-default-props */
import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTable, useFlexLayout, useResizeColumns, usePagination, useSortBy, useFilters } from 'react-table';
import { useSticky } from 'react-table-sticky';
import TableV2Filter from "./TableV2Filter.js";
import { TABLE_TYPES } from "./renderers/index.js";
import { getItemKey } from "./util/index.js";
import Theme from "../Theme.js";
import S from '../TableV2.css';
import BasicTableRenderer from "./renderers/BasicTableRenderer.js";
import { jsx as _jsx } from "react/jsx-runtime";
const EMPTY_OBJ = {};
const EMPTY_ARRAY = [];
function defaultFilterFunc(rows, id, filterValues) {
  const vals = filterValues.map(val => `${val}`.trim());
  return rows.filter(row => {
    return vals.some(filterValue => {
      return filterValue === `${row.values[id]}`.trim();
    });
  });
}
defaultFilterFunc.autoRemove = val => val.length === 0;
TableV2.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  defaultColumn: PropTypes.object,
  variant: PropTypes.string,
  className: PropTypes.string,
  bodyClassName: PropTypes.string,
  beforeHooks: PropTypes.arrayOf(PropTypes.func),
  afterHooks: PropTypes.arrayOf(PropTypes.func),
  initialState: PropTypes.object,
  paginate: PropTypes.bool,
  sortable: PropTypes.bool,
  filterable: PropTypes.bool,
  filterCheckboxClassName: PropTypes.string,
  filterButtonVariant: PropTypes.string,
  filterPaneVariant: PropTypes.string,
  filterButtonIconOn: PropTypes.string,
  filterButtonIconOff: PropTypes.string,
  filterButtonTextOn: PropTypes.string,
  filterButtonTextOff: PropTypes.string,
  filterClearButtonVariant: PropTypes.string,
  filterCancelButtonVariant: PropTypes.string,
  filterOkayButtonVariant: PropTypes.string,
  filterCancelButtonText: PropTypes.string,
  filterOkayButtonText: PropTypes.string,
  filterCheckboxVariant: PropTypes.string,
  filterTextInputVariant: PropTypes.string,
  filterTypes: PropTypes.objectOf(PropTypes.func),
  paginationNextButtonVariant: PropTypes.string,
  paginationNextButtonIcon: PropTypes.string,
  paginationNextButtonText: PropTypes.string,
  paginationPreviousButtonVariant: PropTypes.string,
  paginationPreviousButtonIcon: PropTypes.string,
  paginationPreviousButtonText: PropTypes.string,
  paginationDropdownVariant: PropTypes.string,
  FooterComponent: PropTypes.elementType
};

/**
 * React class that renders a table
 * @class TableV2
 * @see {@link https://react-table.tanstack.com}
 * @param {object} props
 * @param {object[]} props.data - An array of data objects for the table to render
 * @param {object[]} props.columns - An array of column objects for the table to render see {@link https://react-table.tanstack.com/docs/api/useTable#column-options}
 * @param {object} props.defaultColumn - On object specifying default values for table columns. See {@link https://react-table.tanstack.com/docs/api/useTable#table-options}
 * @param {string} props.variant - The TableV2 variant for this instance
 * @param {string} props.className - The className to apply to the TableV2 element
 * @param {string} props.bodyClassName - The classNAme to apply to the TableV2's body element
 * @param {function[]} props.beforeHooks - An array of hook functions to pass to react-table's useTable hook before TableV2 built-in hooks
 * @param {function[]} props.afterHooks - An array of hook functions to pass to react-table's useTable hook after TableV2 built-in hooks
 * @param {object} props.initialState - An object of initial state values to pass to react-table. For example, page size or pageIndex for pagination. See {@link https://react-table.tanstack.com/docs/api/useTable#table-options}
 * @param {boolean} props.paginate - Boolean indicating whether the table should be paginated or infinitely scrolling. Setting this to true
 * will present a pagination and will page rows using the usePagination react-table hook
 * @param {boolean} props.sortable - Whether the table should be sortable or not. Setting this to true will make each
 * column header clickable and will sort the data in the table.
 * @param {boolean} props.filterable - Whether the table should be filterable. If true, the Filter property for each column
 * (or from the defaultColumn prop) will be used to render a filtering UI in each column header. By default this will present
 * a UI to filter table data internally. To manage filtering externally, you must provide your own Filter component for your
 * columns and dispatch events accordingly.
 * @param {string} props.filterButtonVariant - The variant to use for the filter Popup's ToggleButton element
 * @param {string} props.filterPaneVariant - The variant to use for the filter Popup's Pane element
 * @param {string} props.filterButtonIconOn - The on icon class to use for the filter Popup's ToggleButton
 * @param {string} props.filterButtonIconOff - The off icon class to use for the filter Popup's ToggleButton
 * @param {string} props.filterButtonTextOn - The on text to use for the filter Popup's ToggleButton
 * @param {string} props.filterButtonTextOff - The off text to use for the filter Popup's ToggleButton
 * @param {string} props.filterClearButtonVariant - The variant to use for the filter Popup's Clear Button
 * @param {string} props.filterCancelButtonVariant - The variant to use for the filter Popup's Cancel Button
 * @param {string} props.filterOkayButtonVariant - The variant to use for the filter Popup's Okay Button
 * @param {string} props.filterCancelButtonText - The text to use for the filter Popup's Cancel Button
 * @param {string} props.filterOkayButtonText - The text to use for the filter Popup's Okay Button
 * @param {string} props.filterCheckboxVariant - The variant to use for the filter Popup's Checkbox elements
 * @param {string} props.filterTextInputVariant - The variant to use for the filter Popup's TextInput
 * @param {string} props.filterCheckboxLabelVariant - The variant to use for the filter popup's Checkbox Labels
 * @param {object} props.filterTypes - An object whose keys are custom filter function types. These can be used in conjunction
 * with the column option 'filter' to define the filtering behavior for individual columns.
 * See {@link https://react-table.tanstack.com/docs/api/useFilters#usefilters}
 * @param {string} props.paginationNextButtonVariant - The variant to use for the pagination UI's Next Button
 * @param {string} props.paginationNextButtonIcon - The icon class to use for the pagination UI's Next Button
 * @param {string} props.paginationNextButtonText - The text to use for the pagination UI's Next Button
 * @param {string} props.paginationPreviousButtonVariant - The variant to use for the pagination UI's Previous Button
 * @param {string} props.paginationPreviousButtonIcon - The icon class to use for the pagination UI's Previous Button
 * @param {string} props.paginationPreviousButtonText - The text to use for the pagination UI's Previous Button
 * @param {string} props.paginationDropdownVariant - The variant to use for the pagination UI's Dropdowns
 * @param {React.Component} props.FooterComponent - A component to replace the default pagination UI.
 * This component receives:
 *  * canPreviousPage - Boolean representing whether the user can page to a previous page
 *  * canNextPage - Boolean representing whether the user can page to a next page
 *  * pageOptions - Array of page numbers that a user can page to
 *  * pageCount - Integer number of pages in the table
 *  * gotoPage - Function that takes a page number and pages the table
 *  * nextPage - Function that pages to the next page
 *  * previousPage - Function that pages to the previous page
 *  * setPageSize - Function to set the size of each page
 *  * pageIndex - Integer number of the current page
 *  * pageSize - Integer representing the current items per page
 *  * tableInstance - The entire react-table instance object for infinite customization.
 *  See {@link https://react-table.tanstack.com/docs/api/useTable#instance-properties}
 */

export default function TableV2(ownProps) {
  const theme = useContext(Theme.Context);
  const {
    type = 'basic',
    data,
    columns,
    defaultColumn: pDefaultColumn = EMPTY_OBJ,
    variant,
    className,
    bodyClassName,
    beforeHooks = EMPTY_ARRAY,
    afterHooks = EMPTY_ARRAY,
    initialState: pInitialState = EMPTY_OBJ,
    paginate = false,
    sortable = false,
    filterable = false,
    headerFlexVariant,
    filterCheckboxClassName,
    filterButtonVariant,
    filterPaneVariant,
    filterButtonIconOn,
    filterButtonIconOff,
    filterButtonTextOn,
    filterButtonTextOff,
    filterClearButtonVariant,
    filterCancelButtonVariant,
    filterOkayButtonVariant,
    filterCancelButtonText,
    filterOkayButtonText,
    filterCheckboxVariant,
    filterTextInputVariant,
    filterCheckboxLabelVariant,
    filterTypes: pFilterTypes = EMPTY_OBJ,
    paginationNextButtonVariant,
    paginationNextButtonIcon,
    paginationNextButtonText,
    paginationPreviousButtonVariant,
    paginationPreviousButtonIcon,
    paginationPreviousButtonText,
    paginationDropdownVariant,
    FooterComponent,
    getRowProps = (row, props) => {
      return props;
    },
    tableInstanceProps = EMPTY_OBJ,
    loading,
    loadingMessage,
    noDataMessage,
    listRef,
    bodyRef,
    tableRef,
    headerRef,
    onItemsRendered,
    itemKey,
    ...rest
  } = theme.props('TableV2', ownProps.variant, ownProps);
  const filterTypes = useMemo(() => {
    return {
      ...pFilterTypes,
      manyValues: defaultFilterFunc
    };
  }, [pFilterTypes]);
  const defaultColumn = useMemo(() => {
    const defaults = {
      minWidth: 150,
      width: 150,
      filter: 'manyValues',
      Filter: filterable ? props => {
        const {
          state: tableState,
          column,
          preFilteredRows
        } = props;
        const onChangeFilter = ({
          value
        }) => {
          column.setFilter(value);
        };
        return /*#__PURE__*/_jsx(TableV2Filter, {
          preFilteredRows: preFilteredRows,
          column: column,
          tableState: tableState,
          onChangeFilter: onChangeFilter,
          filterCheckboxClassName: filterCheckboxClassName,
          filterButtonVariant: filterButtonVariant,
          filterPaneVariant: filterPaneVariant,
          filterButtonIconOn: filterButtonIconOn,
          filterButtonIconOff: filterButtonIconOff,
          filterButtonTextOn: filterButtonTextOn,
          filterButtonTextOff: filterButtonTextOff,
          filterClearButtonVariant: filterClearButtonVariant,
          filterCancelButtonVariant: filterCancelButtonVariant,
          filterOkayButtonVariant: filterOkayButtonVariant,
          filterCancelButtonText: filterCancelButtonText,
          filterOkayButtonText: filterOkayButtonText,
          filterCheckboxVariant: filterCheckboxVariant,
          filterTextInputVariant: filterTextInputVariant,
          filterCheckboxLabelVariant: filterCheckboxLabelVariant
        });
      } : () => null
    };
    return {
      ...defaults,
      ...pDefaultColumn
    };
  }, [pDefaultColumn, filterButtonVariant, filterPaneVariant, filterButtonIconOn, filterButtonIconOff, filterButtonTextOn, filterButtonTextOff, filterClearButtonVariant, filterCancelButtonVariant, filterOkayButtonVariant, filterCancelButtonText, filterOkayButtonText, filterCheckboxVariant, filterTextInputVariant, filterCheckboxLabelVariant]);
  const getRowId = useCallback((row, relativeIndex, parent) => {
    return getItemKey(row, relativeIndex, itemKey);
  }, [itemKey]);
  const pluginHooks = useMemo(() => {
    const hooks = [...beforeHooks];
    hooks.push(useFilters);
    hooks.push(useSortBy);
    hooks.push(useResizeColumns, useFlexLayout, usePagination, useSticky);
    hooks.push(...afterHooks);
    return hooks;
  }, [beforeHooks, afterHooks]);
  const instance = useTable({
    columns,
    data,
    defaultColumn,
    filterTypes,
    disableFilters: !filterable,
    disableSortBy: !sortable,
    initialState: {
      pageSize: 100,
      ...pInitialState
    },
    autoResetSortBy: false,
    autoResetFilters: false,
    autoResetResize: false,
    getRowId,
    ...tableInstanceProps
  }, ...pluginHooks);
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    rows,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: {
      pageIndex,
      pageSize
    }
  } = instance;
  const tableClassName = theme.classes('TableV2', 'table', variant, className, S.table);
  const tableBodyClassName = theme.subclasses('TableV2', 'body', bodyClassName, S.tbody);
  const tableTheadClassName = theme.subclasses('TableV2', 'thead', S.header);
  const headerGroupClassName = theme.subclasses('TableV2', 'headerGroup');
  const headerClassName = theme.subclasses('TableV2', 'headerCell', S.headerCell);
  const resizerClassName = theme.subclasses('TableV2', 'resizer', S.resizer);
  const rowClassName = theme.subclasses('TableV2', 'row');
  const oddRowClassName = theme.subclasses('TableV2', 'odd');
  const evenRowClassName = theme.subclasses('TableV2', 'even');
  const cellClassName = theme.subclasses('TableV2', 'bodyCell', S.cell);
  const footerClassName = theme.subclasses('TableV2', 'footer', S.footer);
  const sortAscClassName = theme.subclasses('TableV2', 'sortAsc');
  const sortDescClassName = theme.subclasses('TableV2', 'sortDesc');
  const footerProps = useMemo(() => {
    return {
      canPreviousPage,
      canNextPage,
      pageOptions,
      pageCount,
      gotoPage,
      nextPage,
      previousPage,
      setPageSize,
      pageIndex,
      pageSize,
      paginationNextButtonVariant,
      paginationNextButtonIcon,
      paginationNextButtonText,
      paginationPreviousButtonVariant,
      paginationPreviousButtonIcon,
      paginationPreviousButtonText,
      paginationDropdownVariant,
      footerClassName,
      tableInstance: instance
    };
  }, [canPreviousPage, canNextPage, pageOptions, pageCount, gotoPage, nextPage, previousPage, setPageSize, pageIndex, pageSize, paginationNextButtonVariant, paginationNextButtonIcon, paginationNextButtonText, paginationPreviousButtonVariant, paginationPreviousButtonIcon, paginationPreviousButtonText, paginationDropdownVariant, footerClassName, instance]);
  const Component = TABLE_TYPES[type] || BasicTableRenderer;
  return /*#__PURE__*/_jsx(Component, {
    getTableProps: getTableProps,
    tableRef: tableRef,
    tableClassName: tableClassName,
    loading: loading,
    loadingMessage: loadingMessage,
    headerRef: headerRef,
    headerGroups: headerGroups,
    tableTheadClassName: tableTheadClassName,
    headerGroupClassName: headerGroupClassName,
    headerClassName: headerClassName,
    headerFlexVariant: headerFlexVariant,
    resizerClassName: resizerClassName,
    sortAscClassName: sortAscClassName,
    sortDescClassName: sortDescClassName,
    getTableBodyProps: getTableBodyProps,
    bodyRef: bodyRef,
    tableBodyClassName: tableBodyClassName,
    rows: rows,
    listRef: listRef,
    paginate: paginate,
    page: page,
    prepareRow: prepareRow,
    rowClassName: rowClassName,
    evenRowClassName: evenRowClassName,
    oddRowClassName: oddRowClassName,
    cellClassName: cellClassName,
    getRowProps: getRowProps,
    onItemsRendered: onItemsRendered,
    itemKey: itemKey,
    FooterComponent: FooterComponent,
    footerProps: footerProps,
    paginationNextButtonVariant: paginationNextButtonVariant,
    paginationNextButtonIcon: paginationNextButtonIcon,
    paginationNextButtonText: paginationNextButtonText,
    paginationPreviousButtonVariant: paginationPreviousButtonVariant,
    paginationPreviousButtonIcon: paginationPreviousButtonIcon,
    paginationPreviousButtonText: paginationPreviousButtonText,
    paginationDropdownVariant: paginationDropdownVariant,
    noDataMessage: noDataMessage,
    instance: instance,
    ...rest
  });
}